import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  ColumnOneTableCellSkeleton,
  SessionErrorModal,
  Table,
  TableCellSkeleton,
} from "../../../components";
import { SharedSessionColumns } from "../../../components/shared/SharedSessionColumns";
import { useUIContext } from "../../../contexts/UIContext";
import { useCompletedSessionsLoading } from "../../../hooks/useCompletedSessionsLoading";
import { useSessionsTableFilters } from "../../../shared/Constants";
import { parseCompletedSessions } from "../../../shared/Functions";
import { Session } from "../../../types";

export const CompletedSessions: React.FC = (): JSX.Element => {
  // State and AppContext
  const { setDisplayBack } = useUIContext();
  const [selectedPageSize, setSelectedPageSize] = useState(
    sessionStorage.getItem("completedSessionPageSize") || "10",
  );
  const [errorModalSession, setErrorModalSession] = useState<Session | null>(
    null,
  );

  // Loads completed sessions in parallel; org must already be in Redux (from Home)
  const { storedSessionList, fetchingSessions, fetchError } =
    useCompletedSessionsLoading();

  const handleErrorClick = useCallback((session: Session) => {
    setErrorModalSession(session);
  }, []);

  /** Sorts the completed session list by session date in reverse order by default, most recent to oldest */
  const completedSessionList = useMemo(
    () => parseCompletedSessions(storedSessionList || [], [], handleErrorClick),
    [storedSessionList, handleErrorClick],
  );

  const memoizedSharedSessionColumns = useMemo(SharedSessionColumns, []);

  const tableData = useMemo(
    () => (fetchingSessions ? Array(10).fill({}) : completedSessionList),
    [fetchingSessions, completedSessionList],
  );

  const tableColumns = useMemo(
    () =>
      fetchingSessions
        ? memoizedSharedSessionColumns.map((column, index) => ({
            ...column,
            cell:
              index === 0 ? (
                <ColumnOneTableCellSkeleton />
              ) : (
                <TableCellSkeleton />
              ),
          }))
        : memoizedSharedSessionColumns,
    [fetchingSessions, memoizedSharedSessionColumns],
  );

  useEffect(() => {
    setDisplayBack && setDisplayBack(true);
    if (selectedPageSize) {
      sessionStorage.setItem("completedSessionPageSize", selectedPageSize);
    }
  }, [selectedPageSize, setDisplayBack]);

  return (
    <main className="relative pb-20 px-4 z-0 overflow-y-auto w-full">
      <SessionErrorModal
        isOpen={errorModalSession !== null}
        onClose={() => setErrorModalSession(null)}
        session={errorModalSession}
      />
      <div className="mt-8">
        <div className="max-w-8xl mx-auto flex justify-between items-center pb-2">
          <div className="flex justify-start">
            <h2
              className="mt-4 px-4 text-3xl leading-6 font-bold text-gray-900 sm:px-6 lg:px-8 lg:pb-6"
              data-testid="completed-sessions-header"
            >
              Sessions Completed
            </h2>
          </div>
        </div>
        <div className="mt-3" data-testid="completed-sessions-table-container">
          <Table
            columns={tableColumns}
            data={tableData}
            data-testid="completed-sessions-table"
            selectedPageSize={selectedPageSize}
            setSelectedPageSize={setSelectedPageSize}
            displayFooter={true}
            fetching={fetchingSessions}
            fetchError={fetchError}
            customRowClassName="completed-sessions-table-row"
            filters={useSessionsTableFilters()}
          />
        </div>
      </div>
    </main>
  );
};
